import { ChangeDetectorRef, Component, NgZone, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { FormArray, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { forkJoin, Subject } from 'rxjs';
import { distinctUntilChanged, first, takeUntil } from 'rxjs/operators';
import swal from 'sweetalert2';
import { AuthService } from '../../../auth/AuthService';
import { OfficeService } from '../../../Services/office.service';
import {
  AuditErrorCategory,
  AuditErrorType,
  AuditSeverityTag,
  QuestionnairePayload,
  QuestionsService,
  resolveQuestionErrorTypeId,
  severityLabel
} from '../../../Services/questions.service';

type FormKind = 'FULL FORM' | 'SHORT FORM';
type BenefitDomain = 'DENTAL' | 'MEDICAL';
interface QuestionnaireOffice {
  id: number;
  name: string;
  active?: boolean;
  contact?: { email?: string };
  address?: { firstLine?: string; city?: string; state?: string };
}
interface CategoryView {
  name: string;
  questions: any[];
  selected: any[];
  previewQuestions: any[];
  filteredQuestions: any[];
  searchText: string;
  bulkSeverity: string;
  bulkErrorCategoryId: number | null;
  bulkErrorTypeId: number | null;
  severitySummary: string;
  mappingSummary: string;
  missingSeverityCount: number;
  missingMappingCount: number;
}
interface QuestionnaireView {
  label: string;
  formType: FormKind;
  planCreation: string;
  form: FormGroup;
  categories: CategoryView[];
  existing: boolean;
  loading: boolean;
  loaded: boolean;
  saving: boolean;
  error: string;
  requestId: number;
  expandedCategory: number | null;
  notice: string;
}

@Component({
  selector: 'pm-questionnaire',
  templateUrl: './questionnaire.component.html',
  styleUrls: ['./questionnaire.component.css', './questionnaire-audit.css']
})
export class QuestionnaireComponent implements OnInit, OnDestroy {
  officeIds: number[] = [];
  selectedOfficeId: number | null = null;
  offices: QuestionnaireOffice[] = [];
  officeSearch = '';
  officePageIndex = 0;
  officePageSize = 8;
  officePickerOpen = false;
  officeListLoading = false;
  officeListError = '';
  benefitDomain: BenefitDomain = 'MEDICAL';
  templateOfficeId: number | null = null;
  templateNotice = '';
  copyingTemplate = false;
  private officeListRequestId = 0;
  private currentClientId: number | null = null;
  forms: QuestionnaireView[] = [];
  severityOptions: AuditSeverityTag[] = [];
  errorTypeOptions: AuditErrorType[] = [];
  errorCategoryOptions: AuditErrorCategory[] = [];
  loading = false;
  loadError = '';
  severityLabel = severityLabel;
  private catalog: any[] = [];
  private destroyed = new Subject<void>();
  private viewDestroyed = false;
  private refreshQueued = false;

  constructor(private fb: FormBuilder,
              private questionService: QuestionsService,
              private officeService: OfficeService,
              public authService: AuthService,
              private activatedRoute: ActivatedRoute,
              private router: Router,
              private ngZone: NgZone,
              private cdr: ChangeDetectorRef) {
    const navigation = this.router.getCurrentNavigation();
    const passedIds = navigation?.extras?.state?.officeIds;
    const routeId = this.activatedRoute.snapshot.paramMap.get('id');
    let ids: any[] = [];
    if (Array.isArray(passedIds) && passedIds.length) { ids = passedIds; }
    else if (routeId) { ids = [routeId]; }
    else {
      try {
        const stored = JSON.parse(localStorage.getItem('officeIds') || '[]');
        ids = Array.isArray(stored) ? stored : [];
      } catch (_) { ids = []; }
    }
    const validIds = Array.from(new Set(ids.map(Number).filter(id => Number.isInteger(id) && id > 0)));
    this.selectedOfficeId = validIds.length ? validIds[0] : null;
    this.officeIds = this.selectedOfficeId ? [this.selectedOfficeId] : [];
    this.officePickerOpen = !this.selectedOfficeId;
    this.benefitDomain = this.activatedRoute.snapshot.queryParamMap.get('domain')?.toUpperCase() === 'DENTAL'
      ? 'DENTAL' : 'MEDICAL';
    const requestedPlan = this.activatedRoute.snapshot.queryParamMap.get('planCreation');
    const planCreation = ['YES', 'NO', 'BOTH'].includes(requestedPlan) ? requestedPlan : 'NO';
    this.forms = (['FULL FORM', 'SHORT FORM'] as FormKind[]).map(formType => ({
      label: formType === 'FULL FORM' ? 'Full Form' : 'Short Form', formType, planCreation,
      form: this.fb.group({ categories: this.fb.array([]) }), categories: [],
      existing: false, loading: false, loaded: false, saving: false, error: '', requestId: 0,
      expandedCategory: null, notice: ''
    }));
  }

  ngOnInit(): void {
    this.currentClientId = this.authService.getClientId();
    this.loadOffices();
    if (this.selectedOfficeId) { this.loadConfiguration(); }
    this.authService.clientId$.pipe(distinctUntilChanged(), takeUntil(this.destroyed))
      .subscribe(clientId => {
        const nextClientId = clientId ? Number(clientId) : null;
        if (!nextClientId || nextClientId === this.currentClientId) { return; }
        this.currentClientId = nextClientId;
        this.officeListRequestId++;
        this.offices = [];
        this.selectedOfficeId = null;
        this.officeIds = [];
        this.officePickerOpen = true;
        this.templateOfficeId = null;
        this.templateNotice = '';
        this.forms.forEach(state => { state.requestId++; state.loaded = false; state.categories = []; });
        this.catalog = [];
        this.loadError = '';
        this.loading = false;
        this.loadOffices();
      });
  }
  ngOnDestroy(): void {
    this.viewDestroyed = true;
    this.destroyed.next();
    this.destroyed.complete();
  }
  get canEdit(): boolean { return this.authService.hasRole('client-edit'); }
  get anySaving(): boolean { return this.forms.some(state => state.saving); }
  get canChangeContext(): boolean {
    return !this.anySaving && !this.loading && !this.copyingTemplate && !this.forms.some(state => state.loading);
  }
  get filteredOffices(): QuestionnaireOffice[] {
    const term = this.officeSearch.trim().toLowerCase();
    return term ? this.offices.filter(office =>
      (office.name || '').toLowerCase().includes(term) ||
      String(office.id).includes(term) ||
      (office.contact?.email || '').toLowerCase().includes(term)) : this.offices;
  }
  get pagedOffices(): QuestionnaireOffice[] {
    const offset = this.officePageIndex * this.officePageSize;
    return this.filteredOffices.slice(offset, offset + this.officePageSize);
  }
  get officePageCount(): number { return Math.max(1, Math.ceil(this.filteredOffices.length / this.officePageSize)); }
  get selectedOfficeName(): string {
    const office = this.offices.find(item => item.id === this.selectedOfficeId);
    return office ? office.name : this.selectedOfficeId ? 'Office #' + this.selectedOfficeId : 'No office selected';
  }
  officeAddress(office: QuestionnaireOffice): string {
    const a = office.address || {};
    return [a.firstLine, a.city, a.state].filter(Boolean).join(', ') || '-';
  }
  trackByOfficeId(index: number, office: QuestionnaireOffice): number { return office.id; }
  searchOffices(term: string): void { this.officeSearch = term; this.officePageIndex = 0; }
  changeOfficePage(delta: number): void {
    this.officePageIndex = Math.max(0, Math.min(this.officePageCount - 1, this.officePageIndex + delta));
  }
  private loadOffices(page = 1, accumulated: QuestionnaireOffice[] = [], requestId?: number): void {
    const request = requestId === undefined ? ++this.officeListRequestId : requestId;
    const clientId = this.currentClientId || this.authService.getClientId();
    if (!clientId) { this.officeListLoading = false; return; }
    if (page === 1) { this.officeListLoading = true; this.officeListError = ''; this.offices = []; }
    this.officeService.getAllOffices({ page, size: 200, clientId, isOfficeListing: true })
      .pipe(first(), takeUntil(this.destroyed)).subscribe((response: any) => this.updateView(() => {
        if (request !== this.officeListRequestId) { return; }
        const rows = Array.isArray(response?.content) ? response.content : [];
        const all = accumulated.concat(rows.map(office => ({ ...office, id: Number(office.id) }))
          .filter(office => Number.isInteger(office.id) && office.id > 0));
        const total = Number(response?.totalElements) || all.length;
        if (all.length < total && rows.length && page < 100) { this.loadOffices(page + 1, all, request); return; }
        this.offices = all.sort((a,b) => (a.name || '').localeCompare(b.name || ''));
        this.officeListLoading = false;
        if (all.length < total) { this.officeListError = 'Only part of the office list could be loaded.'; }
        if (this.selectedOfficeId && !this.offices.some(office => office.id === this.selectedOfficeId)) {
          this.selectedOfficeId = null;
          this.officeIds = [];
          this.officePickerOpen = true;
          this.forms.forEach(state => { state.requestId++; state.loaded = false; });
        }
      }), error => this.updateView(() => {
        if (request !== this.officeListRequestId) { return; }
        this.officeListLoading = false;
        this.officeListError = this.errorMessage(error);
      }));
  }
  private confirmDiscardChanges(apply: () => void): void {
    if (!this.forms.some(state => state.form.dirty)) { apply(); return; }
    swal.fire({ title: 'Discard unsaved questionnaire changes?',
      text: 'Question order, category weights and audit mappings have not been saved.',
      icon: 'warning', showCancelButton: true, confirmButtonText: 'Discard and continue' })
      .then(result => { if (result.isConfirmed) { this.updateView(apply); } });
  }
  selectOffice(officeId: number): void {
    if (!this.canChangeContext || !Number.isInteger(officeId) || officeId <= 0) { return; }
    if (this.selectedOfficeId === officeId) { this.officePickerOpen = false; return; }
    this.confirmDiscardChanges(() => {
      this.selectedOfficeId = officeId;
      this.officeIds = [officeId];
      this.templateOfficeId = null;
      this.templateNotice = '';
      this.officePickerOpen = false;
      this.loadConfiguration();
    });
  }
  changeDomain(value: string, select: HTMLSelectElement): void {
    if (!this.canChangeContext || (value !== 'MEDICAL' && value !== 'DENTAL')) {
      select.value = this.benefitDomain; return;
    }
    if (value === this.benefitDomain) { return; }
    this.confirmDiscardChanges(() => {
      this.benefitDomain = value as BenefitDomain;
      this.templateNotice = '';
      this.loadConfiguration();
    });
  }
  copyFromOffice(state: QuestionnaireView): void {
    const sourceId = Number(this.templateOfficeId);
    if (!this.canEdit || !this.canChangeContext || !state.loaded || !this.selectedOfficeId ||
        !Number.isInteger(sourceId) || sourceId <= 0 || sourceId === this.selectedOfficeId) { return; }
    this.confirmDiscardChanges(() => {
      const target = this.selectedOfficeId, domain = this.benefitDomain;
      const originalPlan = state.planCreation;
      const plan = originalPlan === 'BOTH' ? 'YES' : originalPlan;
      const formType = state.formType;
      this.copyingTemplate = true;
      this.templateNotice = '';
      this.questionService.getConfiguredQuestionnaire({
        officeId: sourceId, planCreation: plan, formType, domain
      }).pipe(first(), takeUntil(this.destroyed)).subscribe(questions => this.updateView(() => {
        this.copyingTemplate = false;
        if (this.selectedOfficeId !== target || this.benefitDomain !== domain ||
            state.formType !== formType || state.planCreation !== originalPlan) { return; }
        if (!questions.length) { this.templateNotice = 'The source office has no ' + state.label + ' questions.'; return; }
        this.buildForm(state, questions.filter(q => this.matchesDomain(q?.benefitDomain)));
        state.loaded = true;
        state.form.markAsDirty();
        this.templateNotice = 'Questions and audit mappings copied from office #' + sourceId + '. Review and save.';
      }), error => this.updateView(() => { this.copyingTemplate = false; this.templateNotice = this.errorMessage(error); }));
    });
  }
  categoryControls(state: QuestionnaireView): FormArray { return state.form.get('categories') as FormArray; }
  severityControl(state: QuestionnaireView, index: number, id: number): FormControl {
    return this.categoryControls(state).at(index).get(['severities', String(id)]) as FormControl;
  }
  errorCategoryControl(state: QuestionnaireView, index: number, id: number): FormControl {
    return this.categoryControls(state).at(index).get(['errorCategories', String(id)]) as FormControl;
  }
  errorTypeControl(state: QuestionnaireView, index: number, id: number): FormControl {
    return this.categoryControls(state).at(index).get(['errorTypes', String(id)]) as FormControl;
  }
  isActiveSeverity(name: string): boolean { return this.severityOptions.some(tag => tag.name === name); }
  isActiveErrorCategory(id: number): boolean {
    return this.errorCategoryOptions.some(item => item.id === Number(id) && item.active);
  }
  isActiveErrorType(id: number): boolean {
    return this.errorTypeOptions.some(item => item.id === Number(id) && item.active);
  }
  getErrorCategory(id: number | null | undefined): AuditErrorCategory | null {
    return this.errorCategoryOptions.find(item => item.id === Number(id)) || null;
  }
  getErrorType(id: number | null | undefined): AuditErrorType | null {
    return this.errorTypeOptions.find(item => item.id === Number(id)) || null;
  }
  errorTypeLocked(state: QuestionnaireView, index: number, id: number): boolean {
    const category = this.getErrorCategory(this.errorCategoryControl(state, index, id).value);
    return category?.defaultErrorTypeId !== null && category?.defaultErrorTypeId !== undefined;
  }
  bulkErrorTypeLocked(category: CategoryView): boolean {
    const selected = this.getErrorCategory(category.bulkErrorCategoryId);
    return selected?.defaultErrorTypeId !== null && selected?.defaultErrorTypeId !== undefined;
  }
  trackQuestion(index: number, question: any): number { return question.id; }

  // Auth/API and dialog callbacks can complete outside Angular's zone. Update the
  // model in the zone, then coalesce a view refresh after the current stack ends.
  // Never run detectChanges from a lifecycle checking hook or after destruction.
  private updateView(update: () => void): void {
    if (this.viewDestroyed) { return; }
    this.ngZone.run(() => {
      update();
      this.cdr.markForCheck();
      if (this.refreshQueued) { return; }
      this.refreshQueued = true;
      Promise.resolve().then(() => {
        this.refreshQueued = false;
        if (!this.viewDestroyed) { this.cdr.detectChanges(); }
      });
    });
  }

  loadConfiguration(): void {
    if (this.anySaving || this.loading) { return; }
    if (!this.officeIds.length) { this.loadError = ''; return; }
    this.loading = true;
    this.loadError = '';
    this.forms.forEach(state => { state.loaded = false; state.requestId++; });
    forkJoin([
      this.questionService.getquestions({ domain: this.benefitDomain }),
      this.questionService.getSeverityTags(),
      this.questionService.getErrorTypes(),
      this.questionService.getErrorCategories()
    ]).pipe(first(), takeUntil(this.destroyed)).subscribe(
      ([catalog, tags, errorTypes, errorCategories]) => this.updateView(() => {
        if (!Array.isArray(catalog)) {
          this.loading = false;
          this.loadError = 'Unexpected question catalogue response. Please retry.';
          return;
        }
        this.catalog = this.buildDomainCatalog(catalog);
        this.severityOptions = tags.filter(tag => tag.active);
        this.errorTypeOptions = errorTypes.filter(item => item.active);
        this.errorCategoryOptions = errorCategories.filter(item => item.active);
        this.loading = false;
        this.forms.forEach(state => this.loadForm(state));
      }),
      error => this.updateView(() => { this.loading = false; this.loadError = this.errorMessage(error); })
    );
  }

  loadForm(state: QuestionnaireView): void {
    if (this.anySaving || this.loading || this.loadError) { return; }
    const requestId = ++state.requestId;
    state.loading = true;
    state.loaded = false;
    state.error = '';
    state.notice = '';
    state.expandedCategory = null;
    this.questionService.getConfiguredQuestionnaire({
      officeId: this.officeIds[0], formType: state.formType,
      planCreation: state.planCreation === 'BOTH' ? 'YES' : state.planCreation,
      domain: this.benefitDomain
    }).pipe(first(), takeUntil(this.destroyed)).subscribe(questions => this.updateView(() => {
      if (requestId !== state.requestId) { return; }
      try {
        this.buildForm(state, questions.filter(q => this.matchesDomain(q?.benefitDomain)));
        state.existing = questions.length > 0;
        state.loaded = true;
      } catch (error) { state.error = this.errorMessage(error); }
      state.loading = false;
    }), error => this.updateView(() => {
      if (requestId !== state.requestId) { return; }
      state.loading = false;
      state.error = this.errorMessage(error);
    }));
  }

  changePlan(state: QuestionnaireView, planCreation: string): void {
    if (this.anySaving || state.loading || state.planCreation === planCreation) { return; }
    const apply = () => this.updateView(() => { state.planCreation = planCreation; this.loadForm(state); });
    if (!state.form.dirty) { apply(); return; }
    swal.fire({ title: 'Reload configuration?', text: 'Unsaved changes in this form will be discarded.', icon: 'warning',
      showCancelButton: true, confirmButtonText: 'Reload' }).then(result => {
        if (result.isConfirmed) { apply(); }
      });
  }

  private matchesDomain(domain: any): boolean {
    return domain == null || String(domain).toUpperCase() === this.benefitDomain;
  }
  private normalizeCategory(name: any): string {
    return String(name || '').trim().replace(/\s+/g, ' ').toUpperCase();
  }
  private buildDomainCatalog(raw: any[]): any[] {
    const categories = new Map<string, any>();
    raw.forEach(row => {
      if (!this.matchesDomain(row?.benefitDomain)) { return; }
      const name = String(row?.category || '').trim();
      const key = this.normalizeCategory(name);
      if (!key) { return; }
      if (!categories.has(key)) { categories.set(key, { category: name, questionDTO: [] }); }
      const bucket = categories.get(key);
      (Array.isArray(row.questionDTO) ? row.questionDTO : []).forEach(question => {
        const id = Number(question?.id);
        if (!Number.isInteger(id) || !this.matchesDomain(question?.benefitDomain)) { return; }
        if (!bucket.questionDTO.some(item => item.id === id)) { bucket.questionDTO.push({ ...question, id }); }
      });
    });
    return Array.from(categories.values());
  }
  reorderQuestions(event: CdkDragDrop<any[]>, state: QuestionnaireView, index: number): void {
    if (!this.canEdit || !this.canChangeContext || state.categories[index].searchText ||
        event.previousIndex === event.currentIndex) { return; }
    const category = state.categories[index];
    moveItemInArray(category.selected, event.previousIndex, event.currentIndex);
    const group = this.categoryControls(state).at(index);
    group.get('questionsInput').setValue(category.selected.map(question => question.id), { emitEvent: false });
    group.get('questionsInput').markAsDirty();
    state.form.markAsDirty();
    state.notice = '';
    this.refreshCategorySummary(state, index);
    this.cdr.markForCheck();
  }

  private createCategory(name: string, questions: any[] = []): CategoryView {
    return {
      name,
      questions,
      selected: [],
      previewQuestions: [],
      filteredQuestions: [],
      searchText: '',
      bulkSeverity: '',
      bulkErrorCategoryId: null,
      bulkErrorTypeId: null,
      severitySummary: '',
      mappingSummary: '',
      missingSeverityCount: 0,
      missingMappingCount: 0
    };
  }

  private buildForm(state: QuestionnaireView, configured: any[]): void {
    const categories = this.catalog.map(item => this.createCategory(item.category,
      (item.questionDTO || []).map(question => ({ ...question, id: Number(question.id) }))));
    // Retain configured questions absent from the current catalogue rather than silently deleting them.
    configured.forEach(question => {
      let category = categories.find(item => this.normalizeCategory(item.name) === this.normalizeCategory(question.category));
      if (!category) {
        category = this.createCategory(question.category);
        categories.push(category);
      }
      if (!category.questions.some(item => item.id === Number(question.id))) {
        category.questions.push({ ...question, id: Number(question.id) });
      }
    });
    const controls = categories.map(category => {
      const saved = configured.filter(question => this.normalizeCategory(question.category) === this.normalizeCategory(category.name));
      const selectedIds = saved.map(question => Number(question.id));
      const severityControls: { [id: string]: FormControl } = {};
      const errorCategoryControls: { [id: string]: FormControl } = {};
      const errorTypeControls: { [id: string]: FormControl } = {};
      category.questions = category.questions.map(question => {
        const existing = saved.find(item => Number(item.id) === question.id);
        const merged = existing ? { ...question, ...existing, id: question.id } : question;
        const mappedCategory = this.getErrorCategory(merged.errorCategoryId);
        const mappedErrorTypeId = resolveQuestionErrorTypeId(mappedCategory, merged.errorTypeId);
        const selected = selectedIds.includes(question.id);

        severityControls[String(question.id)] = new FormControl({
          value: merged.severity || null, disabled: !this.canEdit || !selected
        }, [Validators.required, control =>
          !control.value || this.isActiveSeverity(control.value) ? null : { inactiveSeverity: true }]);

        errorCategoryControls[String(question.id)] = new FormControl({
          value: merged.errorCategoryId ? Number(merged.errorCategoryId) : null,
          disabled: !this.canEdit || !selected
        }, [Validators.required, control =>
          !control.value || this.isActiveErrorCategory(control.value) ? null : { inactiveErrorCategory: true }]);

        errorTypeControls[String(question.id)] = new FormControl({
          value: mappedErrorTypeId,
          disabled: !this.canEdit || !selected || Boolean(mappedCategory?.defaultErrorTypeId)
        }, [Validators.required, control =>
          !control.value || this.isActiveErrorType(control.value) ? null : { inactiveErrorType: true }]);

        return { ...merged, errorTypeId: mappedErrorTypeId };
      });
      category.selected = selectedIds.map(id => category.questions.find(question => question.id === id));
      const weighted = saved.find(question => question.categoryWeight !== null && question.categoryWeight !== undefined);
      return this.fb.group({
        questionsInput: new FormControl({ value: selectedIds, disabled: !this.canEdit }),
        categoryWeight: new FormControl({ value: weighted ? weighted.categoryWeight : null,
          disabled: !this.canEdit || !selectedIds.length },
          [Validators.required, Validators.min(0), Validators.max(100), Validators.pattern(/^\d+(\.\d{1,6})?$/)]),
        severities: this.fb.group(severityControls),
        errorCategories: this.fb.group(errorCategoryControls),
        errorTypes: this.fb.group(errorTypeControls)
      });
    });
    state.categories = categories;
    state.form = this.fb.group({ categories: this.fb.array(controls) });
    categories.forEach((category, index) => this.refreshCategorySummary(state, index));
  }

  toggleCategory(state: QuestionnaireView, index: number): void {
    if (!state.loaded || state.loading || this.anySaving) { return; }
    state.expandedCategory = state.expandedCategory === index ? null : index;
  }

  filterCategoryQuestions(category: CategoryView, searchText: string): void {
    category.searchText = searchText;
    const query = searchText.trim().toLowerCase();
    category.filteredQuestions = query ? category.selected.filter(question =>
      `${question.question || ''} ${question.questionCode || ''}`.toLowerCase().includes(query)) : category.selected.slice();
  }

  private refreshCategorySummary(state: QuestionnaireView, index: number): void {
    const category = state.categories[index];
    const counts: { [name: string]: number } = Object.create(null);
    category.missingSeverityCount = 0;
    category.missingMappingCount = 0;
    const categoryNames = new Set<string>();
    const typeNames = new Set<string>();
    category.selected.forEach(question => {
      const severity = this.severityControl(state, index, question.id).value;
      if (!this.isActiveSeverity(severity)) { category.missingSeverityCount++; }
      else { counts[severity] = (counts[severity] || 0) + 1; }

      const errorCategory = this.getErrorCategory(this.errorCategoryControl(state, index, question.id).value);
      const errorType = this.getErrorType(this.errorTypeControl(state, index, question.id).value);
      if (!errorCategory || !errorType) {
        category.missingMappingCount++;
      } else {
        categoryNames.add(errorCategory.name);
        typeNames.add(errorType.name);
      }
    });
    const summary = Object.keys(counts).map(name => `${severityLabel(name)}: ${counts[name]}`);
    if (category.missingSeverityCount) { summary.push(`Needs severity: ${category.missingSeverityCount}`); }
    category.severitySummary = summary.join(' | ') || 'No questions selected';
    category.mappingSummary = category.missingMappingCount
      ? `Needs error mapping: ${category.missingMappingCount}`
      : category.selected.length
        ? `${categoryNames.size} error categor${categoryNames.size === 1 ? 'y' : 'ies'} / ${typeNames.size} error type${typeNames.size === 1 ? '' : 's'}`
        : 'No questions selected';
    category.previewQuestions = category.selected.slice(0, 4);
    this.filterCategoryQuestions(category, category.searchText);
  }

  applyCategorySeverity(state: QuestionnaireView, index: number): void {
    const category = state.categories[index];
    if (!this.canEdit || this.anySaving || !state.loaded || !category.selected.length || !this.isActiveSeverity(category.bulkSeverity)) { return; }
    // Only the selected questions in this category/form are changed. This does
    // not alter the master catalogue, other categories, or unselected questions.
    category.selected.forEach(question => {
      const control = this.severityControl(state, index, question.id);
      control.setValue(category.bulkSeverity, { onlySelf: true, emitEvent: false });
      control.markAsDirty();
      control.markAsTouched();
    });
    this.categoryControls(state).at(index).get('severities').updateValueAndValidity();
    this.refreshCategorySummary(state, index);
    state.notice = `${severityLabel(category.bulkSeverity)} applied to all ${category.selected.length} selected questions in ${category.name}. Save the questionnaire to keep these changes.`;
    this.cdr.markForCheck();
  }

  changeQuestionSeverity(state: QuestionnaireView, index: number, id: number, severity: string): void {
    if (!this.canEdit || this.anySaving || !this.isActiveSeverity(severity) ||
        !state.categories[index].selected.some(question => question.id === id)) { return; }
    const control = this.severityControl(state, index, id);
    control.setValue(severity);
    control.markAsDirty();
    control.markAsTouched();
    state.notice = '';
    this.refreshCategorySummary(state, index);
  }

  changeBulkErrorCategory(category: CategoryView, value: any): void {
    const errorCategoryId = value === '' || value === null || value === undefined ? null : Number(value);
    category.bulkErrorCategoryId = errorCategoryId;
    category.bulkErrorTypeId = resolveQuestionErrorTypeId(
      this.getErrorCategory(errorCategoryId),
      category.bulkErrorTypeId
    );
  }

  applyCategoryErrorMapping(state: QuestionnaireView, index: number): void {
    const category = state.categories[index];
    const errorCategory = this.getErrorCategory(category.bulkErrorCategoryId);
    const resolvedErrorTypeId = resolveQuestionErrorTypeId(errorCategory, category.bulkErrorTypeId);

    if (!this.canEdit || this.anySaving || !state.loaded || !category.selected.length ||
        !errorCategory || !resolvedErrorTypeId || !this.isActiveErrorType(resolvedErrorTypeId)) {
      return;
    }

    category.selected.forEach(question => {
      const categoryControl = this.errorCategoryControl(state, index, question.id);
      const typeControl = this.errorTypeControl(state, index, question.id);
      categoryControl.setValue(errorCategory.id, { emitEvent: false });
      categoryControl.markAsDirty();
      categoryControl.markAsTouched();
      typeControl.setValue(resolvedErrorTypeId, { emitEvent: false });
      typeControl.markAsDirty();
      typeControl.markAsTouched();

      if (errorCategory.defaultErrorTypeId) {
        typeControl.disable({ emitEvent: false });
      } else if (this.canEdit) {
        typeControl.enable({ emitEvent: false });
      }
    });

    this.categoryControls(state).at(index).updateValueAndValidity();
    this.refreshCategorySummary(state, index);
    state.notice = `${errorCategory.name} / ${this.getErrorType(resolvedErrorTypeId)?.name || ''} applied to all ${category.selected.length} selected questions in ${category.name}. Save the questionnaire to keep these changes.`;
    this.cdr.markForCheck();
  }

  changeQuestionErrorCategory(state: QuestionnaireView, index: number, id: number, value: any): void {
    if (!this.canEdit || this.anySaving || !state.categories[index].selected.some(question => question.id === id)) {
      return;
    }

    const errorCategoryId = Number(value);
    if (!this.isActiveErrorCategory(errorCategoryId)) { return; }

    const categoryControl = this.errorCategoryControl(state, index, id);
    const typeControl = this.errorTypeControl(state, index, id);
    const errorCategory = this.getErrorCategory(errorCategoryId);
    const resolvedErrorTypeId = resolveQuestionErrorTypeId(errorCategory, typeControl.value);

    categoryControl.setValue(errorCategoryId);
    categoryControl.markAsDirty();
    categoryControl.markAsTouched();
    typeControl.setValue(resolvedErrorTypeId);

    if (errorCategory?.defaultErrorTypeId) {
      typeControl.disable({ emitEvent: false });
    } else {
      typeControl.enable({ emitEvent: false });
    }

    typeControl.markAsDirty();
    typeControl.markAsTouched();
    state.notice = '';
    this.refreshCategorySummary(state, index);
  }

  changeQuestionErrorType(state: QuestionnaireView, index: number, id: number, value: any): void {
    if (!this.canEdit || this.anySaving || this.errorTypeLocked(state, index, id) ||
        !state.categories[index].selected.some(question => question.id === id)) {
      return;
    }

    const errorTypeId = Number(value);
    if (!this.isActiveErrorType(errorTypeId)) { return; }

    const control = this.errorTypeControl(state, index, id);
    control.setValue(errorTypeId);
    control.markAsDirty();
    control.markAsTouched();
    state.notice = '';
    this.refreshCategorySummary(state, index);
  }

  selectionChanged(state: QuestionnaireView, index: number, selectedIds: number[]): void {
    if (!this.canEdit || this.anySaving) { return; }
    const category = state.categories[index];
    const previous = category.selected.map(question => question.id);
    const ordered = previous.filter(id => selectedIds.includes(id))
      .concat(selectedIds.filter(id => !previous.includes(id)));
    category.selected = ordered.map(id => category.questions.find(question => question.id === id));
    const group = this.categoryControls(state).at(index) as FormGroup;
    group.get('questionsInput').setValue(ordered, { emitEvent: false });
    group.get('questionsInput').markAsDirty();
    const weight = group.get('categoryWeight');
    if (ordered.length) { weight.enable({ emitEvent: false }); } else { weight.disable({ emitEvent: false }); }
    category.questions.forEach(question => {
      const selected = ordered.includes(question.id);
      const severity = this.severityControl(state, index, question.id);
      const errorCategory = this.errorCategoryControl(state, index, question.id);
      const errorType = this.errorTypeControl(state, index, question.id);

      if (selected) {
        severity.enable({ emitEvent: false });
        errorCategory.enable({ emitEvent: false });
        const configuredCategory = this.getErrorCategory(errorCategory.value);
        if (configuredCategory?.defaultErrorTypeId) {
          errorType.setValue(configuredCategory.defaultErrorTypeId, { emitEvent: false });
          errorType.disable({ emitEvent: false });
        } else {
          errorType.enable({ emitEvent: false });
        }
      } else {
        severity.disable({ emitEvent: false });
        errorCategory.disable({ emitEvent: false });
        errorType.disable({ emitEvent: false });
      }
    });
    group.markAsDirty();
    group.updateValueAndValidity();
    state.notice = '';
    this.refreshCategorySummary(state, index);
  }

  removeQuestion(state: QuestionnaireView, index: number, id: number): void {
    this.selectionChanged(state, index, state.categories[index].selected.filter(question => question.id !== id).map(question => question.id));
  }

  totalWeight(state: QuestionnaireView): number {
    const total = state.categories.reduce((sum, category, index) => {
      const value = Number(this.categoryControls(state).at(index).get('categoryWeight').value);
      return sum + (category.selected.length && Number.isFinite(value) ? value : 0);
    }, 0);
    return Math.round(total * 1000000) / 1000000;
  }
  selectedCount(state: QuestionnaireView): number {
    return state.categories.reduce((sum, category) => sum + category.selected.length, 0);
  }
  missingErrorMappingCount(state: QuestionnaireView): number {
    return state.categories.reduce((sum, category) => sum + category.missingMappingCount, 0);
  }
  missingSeverityCount(state: QuestionnaireView): number {
    return state.categories.reduce((sum, category) => sum + category.missingSeverityCount, 0);
  }
  canSave(state: QuestionnaireView): boolean {
    return this.canEdit && state.loaded && !state.loading && !this.loading && !this.loadError && !this.anySaving &&
      !state.form.invalid && this.selectedCount(state) > 0 && this.totalWeight(state) <= 100 &&
      this.missingErrorMappingCount(state) === 0;
  }
  showQuestionWeight(state: QuestionnaireView, index: number, question: any): boolean {
    const group = this.categoryControls(state).at(index);
    return !group.get('categoryWeight').dirty && !group.get('questionsInput').dirty &&
      question.questionWeight !== null && question.questionWeight !== undefined;
  }

  save(state: QuestionnaireView): void {
    state.form.markAllAsTouched();
    if (!this.canSave(state)) { return; }
    const payload: QuestionnairePayload = {
      officeIds: this.officeIds.slice(), formType: state.formType, planCreation: state.planCreation,
       domain: this.benefitDomain,
      categoryWeights: {}, question: []
    };
    state.categories.forEach((category, index) => {
      // Explicit zero clears a removed category's prior weight; no per-question weight is sent.
      payload.categoryWeights[category.name] = category.selected.length
        ? Number(this.categoryControls(state).at(index).get('categoryWeight').value) : 0;
      category.selected.forEach(question => payload.question.push({
        id: question.id, category: category.name, question: question.question,
        questionCode: question.questionCode || null,
        severity: this.severityControl(state, index, question.id).value,
        errorCategoryId: Number(this.errorCategoryControl(state, index, question.id).value),
        errorTypeId: Number(this.errorTypeControl(state, index, question.id).value)
      }));
    });
    state.saving = true;
    state.error = '';
    state.notice = '';
    this.questionService.saveQuestionnaire(payload).pipe(first(), takeUntil(this.destroyed)).subscribe(results => this.updateView(() => {
      state.saving = false;
      const failures = results.filter(result => !result.success);
      if (failures.length) {
        const successes = results.filter(result => result.success).reduce((sum, result) => sum + result.officeIds.length, 0);
        state.error = (successes ? `${successes} office/plan configuration(s) saved. ` : '') + failures.map(result =>
          `Office(s) ${result.officeIds.join(', ')} / ${result.planCreation}: ${this.errorMessage(result.error)}`).join(' ');
        return;
      }
      swal.fire({ title: 'Saved', icon: 'success', text: `${state.label} questionnaire saved successfully.`,
        showConfirmButton: false, timer: 2500 });
      this.loadForm(state);
    }), error => this.updateView(() => { state.saving = false; state.error = this.errorMessage(error); }));
  }

  private errorMessage(error: any): string {
    return error?.error?.errors?.[0] || error?.error?.message || error?.message || 'Unable to complete the request. Please retry.';
  }
}
